import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';
import SideBar from './SideBar';
import authApi from '../auth.api';
import {  ALL_VIDEOS } from '../route';
import VideoShowcaseButton from './series/my-series/video-showcase/VideoShowcaseButton';
import {
  Pause,
  Play,
  Volume2,
  VolumeX,
  X,
  Youtube,
  Video as TikTokIcon,
  Upload,
  Instagram,
  Copy,
  Check
} from 'lucide-react';

// Separate into a reusable component
const VideoCard = ({ video,  }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const videoRef = React.useRef(null);

  useEffect(() => {
    if (videoRef.current) {
      if (isHovered && !isPlaying) {
        videoRef.current.play().catch(() => {
          // Handle autoplay failure
          console.log('Autoplay prevented');
        });
        setIsPlaying(true);
      } else if (!isHovered && isPlaying) {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  }, [isHovered, isPlaying]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
      <div
          className="relative aspect-[9/16] rounded-lg shadow-md overflow-hidden group"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
      >
        <video
            ref={videoRef}
            src={video.uploadUrl}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
        />
        <AnimatePresence>
          {isHovered && (
              <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent flex flex-col justify-between p-4"
              >
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <Instagram size={16} className={video.isPublishedOnInstagram ? 'text-green-500' : 'text-gray-400'} />
                    <span className="text-xs font-medium text-white">
                  {video.isPublishedOnInstagram ? 'Published' : 'Not Published'}
                </span>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Instagram size={16} className={video.isPublishedOnInstagram ? 'text-green-500' : 'text-gray-400'} />
                      <span className="text-xs font-medium text-white">
                    {video.isPublishedOnInstagram ? 'Published' : 'Not Published'}
                  </span>
                    </div>
                    {!video.isPublishedOnInstagram && (
                        <VideoShowcaseButton
                            size="xs"
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white"
                        >
                          <Upload size={12} className="mr-1" />
                          Share to Instagram
                        </VideoShowcaseButton>
                    )}

                    <div className="flex items-center space-x-2">
                      <Youtube size={16} className={video.isPublishedOnYouTube ? 'text-green-500' : 'text-gray-400'} />
                      <span className="text-xs font-medium text-white">
                    {video.isPublishedOnYouTube ? 'Published' : 'Not Published'}
                  </span>
                    </div>
                    {!video.isPublishedOnYouTube && (
                        <VideoShowcaseButton
                            size="xs"
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white"
                        >
                          <Upload size={12} className="mr-1" />
                          Share to YouTube
                        </VideoShowcaseButton>
                    )}

                    <div className="flex items-center space-x-2">
                      <TikTokIcon size={16} className={video.isPublishedOnTikTok ? 'text-green-500' : 'text-gray-400'} />
                      <span className="text-xs font-medium text-white">
                    {video.isPublishedOnTikTok ? 'Published' : 'Not Published'}
                  </span>
                    </div>
                    {!video.isPublishedOnTikTok && (
                        <VideoShowcaseButton
                            size="xs"
                            variant="secondary"
                            className="bg-white/20 hover:bg-white/30 text-white"
                        >
                          <Upload size={12} className="mr-1" />
                          Share to TikTok
                        </VideoShowcaseButton>
                    )}

                    <VideoShowcaseButton
                        size="xs"
                        variant="secondary"
                        onClick={() => {
                          navigator.clipboard.writeText(video.uploadUrl);
                          setIsCopied(true);
                          setTimeout(() => setIsCopied(false), 2000);
                        }}
                        className="bg-white/20 hover:bg-white/30 text-white"
                    >
                      {isCopied ? (
                          <Check size={12} className="mr-1 text-green-400" />
                      ) : (
                          <Copy size={12} className="mr-1" />
                      )}
                      {isCopied ? 'Copied!' : 'Copy URL'}
                    </VideoShowcaseButton>

                    <div className="text-xs text-white/70">
                      <p>{video.userEmail}</p>
                      <p>{formatDate(video.createdAt)}</p>
                    </div>
                  </div>

                  <div className="text-xs text-white/70">
                    <p>{video.userEmail}</p>
                    <p>{formatDate(video.createdAt)}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h3 className="text-white text-sm font-semibold line-clamp-1">
                    {video.title}
                  </h3>
                  <p className="text-white/80 text-xs line-clamp-2">
                    {video.description}
                  </p>
                </div>
              </motion.div>
          )}
        </AnimatePresence>
      </div>
  );
};

const FullscreenVideo = ({ video, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [isCopied, setIsCopied] = useState(false);
  const videoRef = React.useRef(null);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  return (
      <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-50"
      >
        <div className="relative w-full max-w-4xl aspect-video">
          <video
              ref={videoRef}
              src={video.uploadUrl}
              className="w-full h-full object-contain rounded-lg shadow-2xl"
              loop
              muted={isMuted}
              playsInline
              autoPlay
          />

          <div className="absolute top-4 right-4">
            <VideoShowcaseButton
                variant="secondary"
                size="sm"
                onClick={onClose}
                className="bg-white/20 hover:bg-white/30"
            >
              <X className="h-4 w-4" />
            </VideoShowcaseButton>
          </div>

          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
            <h3 className="text-xl font-semibold text-white">{video.title}</h3>
            <p className="text-sm text-white/80 mt-1">{video.description}</p>

            <div className="flex justify-between items-center mt-4">
              <div className="flex space-x-4">
                <div className="flex items-center space-x-1">
                  <Instagram size={16} className={video.isPublishedOnInstagram ? 'text-green-500' : 'text-gray-400'} />
                  <span className="text-xs font-medium text-white">
                  {video.isPublishedOnInstagram ? 'Published' : 'Not Published'}
                </span>
                </div>
              </div>

              <div className="flex space-x-2">
                <VideoShowcaseButton
                    variant="secondary"
                    size="sm"
                    onClick={togglePlay}
                    className="bg-white/20 hover:bg-white/30"
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </VideoShowcaseButton>
                <VideoShowcaseButton
                    variant="secondary"
                    size="sm"
                    onClick={toggleMute}
                    className="bg-white/20 hover:bg-white/30"
                >
                  {isMuted ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </VideoShowcaseButton>
                <VideoShowcaseButton
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(video.uploadUrl);
                      setIsCopied(true);
                      setTimeout(() => setIsCopied(false), 2000);
                    }}
                    className="bg-white/20 hover:bg-white/30"
                >
                  {isCopied ? (
                      <Check size={20} className="text-green-400" />
                  ) : (
                      <Copy size={20} />
                  )}
                </VideoShowcaseButton>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
  );
};

const FastShortsVideos = () => {
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchAllVideos();
  }, []);

  const fetchAllVideos = async () => {
    try {
      const { data } = await authApi.get(ALL_VIDEOS);
      setVideos(data.videos);
    } catch (error) {
      toast.error('Failed to load videos. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
        <div className="flex h-screen bg-gray-50">
          <SideBar />
          <div className="flex-1 flex items-center justify-center">
            <div className="text-gray-500">Loading videos...</div>
          </div>
        </div>
    );
  }

  return (
      <div className="flex h-screen bg-gray-50">
        <SideBar />
        <div className="flex-1 overflow-auto px-6 py-8 md:p-8">
          <div className="max-w-7xl mx-auto space-y-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 tracking-tight">
                Videos
              </h2>
            </div>

            {videos.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No videos found. Videos you create will appear here.
                </div>
            ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4">
                  {videos.map((video) => (
                      <div
                          key={video._id}
                          onClick={() => setSelectedVideo(video)}
                          className="cursor-pointer transform transition hover:scale-105"
                      >
                        <VideoCard
                            video={video}
                        />
                      </div>
                  ))}
                </div>
            )}
          </div>
        </div>

        <AnimatePresence>
          {selectedVideo && (
              <FullscreenVideo
                  video={selectedVideo}
                  onClose={() => setSelectedVideo(null)}
              />
          )}
        </AnimatePresence>
      </div>
  );
};

export default FastShortsVideos;
